import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const wholesaleSchema = z.object({
  company_name: z.string().min(2, "Минимум 2 символа").max(200),
  contact_name: z.string().min(2, "Минимум 2 символа").max(100),
  phone: z.string().min(10, "Введите корректный номер телефона").max(20),
  email: z.string().email("Введите корректный email"),
  city: z.string().min(2, "Минимум 2 символа").max(100),
  message: z.string().max(2000).optional(),
});

const WholesaleForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(wholesaleSchema),
    defaultValues: {
      company_name: "",
      contact_name: "",
      phone: "",
      email: "",
      city: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/wholesale`, data);
      if (response.data.success) {
        toast.success("Заявка отправлена!", {
          description: response.data.message,
        });
        form.reset();
      }
    } catch (error) {
      toast.error("Ошибка", {
        description: error.response?.data?.detail || "Не удалось отправить заявку",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card-soft p-8 md:p-12 bg-[#FFFACD]/30" data-testid="wholesale-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-[#FFFACD] flex items-center justify-center">
          <Building2 className="w-6 h-6 text-[#1A1A1A]" />
        </div>
        <div>
          <h2 className="heading-2" data-testid="wholesale-title">Оптовые заказы</h2>
          <p className="text-sm text-[#4A4A4A]">Специальные условия для партнеров</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="wholesale-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="company_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1A1A1A] font-medium">Название компании *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="ООО Детский мир"
                      className="input-custom"
                      data-testid="wholesale-company-input"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contact_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1A1A1A] font-medium">Контактное лицо *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Иван Иванов"
                      className="input-custom"
                      data-testid="wholesale-contact-input"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1A1A1A] font-medium">Телефон *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="+7 (999) 123-45-67"
                      className="input-custom"
                      data-testid="wholesale-phone-input"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[#1A1A1A] font-medium">Email *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="company@example.com"
                      type="email"
                      className="input-custom"
                      data-testid="wholesale-email-input"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel className="text-[#1A1A1A] font-medium">Город *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Москва"
                      className="input-custom"
                      data-testid="wholesale-city-input"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#1A1A1A] font-medium">Сообщение (необязательно)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Расскажите о вашем бизнесе и планируемых объемах закупки..."
                    className="input-custom min-h-[120px] resize-none"
                    data-testid="wholesale-message-input"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="btn-primary w-full md:w-auto"
            disabled={isSubmitting}
            data-testid="wholesale-submit-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Отправка...
              </>
            ) : (
              "Отправить заявку"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default WholesaleForm;
